"use client";

import {
  Container,
  Title,
  Text,
  Button,
  Stack,
  Box,
  Card,
  Group,
  SimpleGrid,
  ThemeIcon,
  List,
  Grid,
  Image,
} from "@mantine/core";
import {
  IconPalette,
  IconPhoto,
  IconShirt,
  IconStar,
  IconTie,
  IconSun,
  IconLeaf,
  IconSnowflake,
} from "@tabler/icons-react";
import { Link } from "@/i18n/navigation";
import { useTranslations } from "next-intl";

export default function SeasonalColorAnalysisForMenContent() {
  const t = useTranslations("seasonalColorAnalysisForMen");

  const mensSeasons = [
    {
      name: "Spring",
      color: "green",
      icon: IconSun,
      description: t("springDescription"),
      workwear: [t("suitsShirtsLabel") + " Light blue shirts, Khaki suits, Gold ties"],
      casual: [t("casualWearLabel") + " Coral polos, Light green tees, Tan chinos"],
      accessories: [t("accessoriesLabel") + " Gold watches, Brown leather, Warm-toned ties"],
    },
    {
      name: "Summer",
      color: "blue",
      icon: IconSun,
      description: t("summerDescription"),
      workwear: [t("suitsShirtsLabel") + " Navy suits, Light blue shirts, Silver ties"],
      casual: [t("casualWearLabel") + " Lavender polos, Soft blue tees, Gray chinos"],
      accessories: [t("accessoriesLabel") + " Silver watches, Gray leather, Cool-toned ties"],
    },
    {
      name: "Autumn",
      color: "orange",
      icon: IconLeaf,
      description: t("autumnDescription"),
      workwear: [t("suitsShirtsLabel") + " Brown suits, Burgundy shirts, Gold ties"],
      casual: [t("casualWearLabel") + " Burnt orange polos, Olive tees, Brown chinos"],
      accessories: [t("accessoriesLabel") + " Gold watches, Brown leather, Warm-toned ties"],
    },
    {
      name: "Winter",
      color: "violet",
      icon: IconSnowflake,
      description: t("winterDescription"),
      workwear: [t("suitsShirtsLabel") + " Black suits, White shirts, Silver ties"],
      casual: [t("casualWearLabel") + " Royal blue polos, Black tees, Navy chinos"],
      accessories: [t("accessoriesLabel") + " Silver watches, Black leather, Cool-toned ties"],
    },
  ];

  return (
    <Container size="lg" py="xl">
      <Stack gap="xl">
        {/* Hero Section */}
        <Box ta="center">
          <Title order={1} size="h1" mb="md">
            {t("title")}
          </Title>
          <Text size="lg" c="dimmed" maw={600} mx="auto" mb="xl">
            {t("subtitle")}
          </Text>

          {/* Men's Color Analysis Image */}
          <Box mb="xl">
            <Image
              src="/images/generated/men-color-analysis.jpg"
              alt="Color Analysis Guide for Men"
              width={800}
              height={600}
              radius="md"
              style={{ maxWidth: "100%", height: "auto" }}
            />
          </Box>

          <Button
            size="xl"
            component={Link}
            href="/color-analysis-quiz"
            color="violet"
            rightSection={<IconPalette size={20} />}
            mb="xl"
          >
            {t("takeQuizButton")}
          </Button>
        </Box>

        {/* Why it matters */}
        <Card withBorder p="xl" radius="md">
          <Title order={2} mb="md">
            {t("whyMattersTitle")}
          </Title>
          <Text mb="lg">
            {t("whyMattersIntro")}
          </Text>
          <SimpleGrid cols={{ base: 1, md: 3 }} spacing="lg">
            <Box>
              <ThemeIcon size={60} radius="xl" color="blue" variant="light" mb="md">
                <IconShirt size={30} />
              </ThemeIcon>
              <Title order={3} size="h5" mb="sm">
                {t("professionalImageTitle")}
              </Title>
              <Text size="sm" c="dimmed">
                {t("professionalImageDescription")}
              </Text>
            </Box>
            <Box>
              <ThemeIcon size={60} radius="xl" color="green" variant="light" mb="md">
                <IconStar size={30} />
              </ThemeIcon>
              <Title order={3} size="h5" mb="sm">
                {t("confidenceBoostTitle")}
              </Title>
              <Text size="sm" c="dimmed">
                {t("confidenceBoostDescription")}
              </Text>
            </Box>
            <Box>
              <ThemeIcon size={60} radius="xl" color="orange" variant="light" mb="md">
                <IconTie size={30} />
              </ThemeIcon>
              <Title order={3} size="h5" mb="sm">
                {t("easyShoppingTitle")}
              </Title>
              <Text size="sm" c="dimmed">
                {t("easyShoppingDescription")}
              </Text>
            </Box>
          </SimpleGrid>
        </Card>

        {/* Quick ID */}
        <Card withBorder p="xl" radius="md">
          <Title order={2} mb="md">
            {t("quickIDTitle")}
          </Title>
          <Text mb="lg">{t("quickIDIntro")}</Text>
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
            <Box>
              <Title order={3} size="h5" mb="sm" c="blue">
                {t("jewelryTestTitle")}
              </Title>
              <Text size="sm" c="dimmed" mb="md">
                {t("jewelryTestDescription")}
              </Text>
              <List size="sm" spacing="xs">
                <List.Item>{t("jewelryTestGold")}</List.Item>
                <List.Item>{t("jewelryTestSilver")}</List.Item>
                <List.Item>{t("jewelryTestBoth")}</List.Item>
              </List>
            </Box>
            <Box>
              <Title order={3} size="h5" mb="sm" c="green">
                {t("colorTestTitle")}
              </Title>
              <Text size="sm" c="dimmed" mb="md">
                {t("colorTestDescription")}
              </Text>
              <List size="sm" spacing="xs">
                <List.Item>{t("colorTestBlue")}</List.Item>
                <List.Item>{t("colorTestOrange")}</List.Item>
                <List.Item>{t("colorTestBoth")}</List.Item>
              </List>
            </Box>
          </SimpleGrid>
        </Card>

        {/* Workwear palettes */}
        <Card withBorder p="xl" radius="md">
          <Title order={2} mb="md">
            {t("workwearPalettesTitle")}
          </Title>
          <Text mb="lg">{t("workwearPalettesIntro")}</Text>
          <Grid>
            {mensSeasons.map((season) => {
              const SeasonIcon = season.icon;
              return (
                <Grid.Col key={season.name} span={{ base: 12, md: 6 }}>
                  <Card withBorder p="md" radius="md" h="100%">
                    <Group mb="md">
                      <ThemeIcon size={40} radius="xl" color={season.color} variant="light">
                        <SeasonIcon size={20} />
                      </ThemeIcon>
                      <Title order={3} size="h5" c={season.color}>
                        {season.name}
                      </Title>
                    </Group>
                    <Text size="sm" c="dimmed" mb="md">
                      {season.description}
                    </Text>
                    <Box mb="md">
                      <Text size="sm">{season.workwear[0]}</Text>
                    </Box>
                    <Box mb="md">
                      <Text size="sm">{season.casual[0]}</Text>
                    </Box>
                    <Box>
                      <Text size="sm">{season.accessories[0]}</Text>
                    </Box>
                  </Card>
                </Grid.Col>
              );
            })}
          </Grid>
        </Card>

        {/* Casual picks */}
        <Card withBorder p="xl" radius="md">
          <Title order={2} mb="md">
            {t("casualPicksTitle")}
          </Title>
          <Text mb="lg">{t("casualPicksIntro")}</Text>
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
            <Box>
              <Title order={3} size="h5" mb="sm" c="blue">
                {t("coolSeasonsTitle")}
              </Title>
              <List size="sm" spacing="xs" mb="md">
                <List.Item>{t("coolSeasonsList1")}</List.Item>
                <List.Item>{t("coolSeasonsList2")}</List.Item>
                <List.Item>{t("coolSeasonsList3")}</List.Item>
                <List.Item>{t("coolSeasonsList4")}</List.Item>
                <List.Item>{t("coolSeasonsList5")}</List.Item>
              </List>
              <Text size="sm" c="dimmed">
                {t("coolSeasonsDescription")}
              </Text>
            </Box>
            <Box>
              <Title order={3} size="h5" mb="sm" c="orange">
                {t("warmSeasonsTitle")}
              </Title>
              <List size="sm" spacing="xs" mb="md">
                <List.Item>{t("warmSeasonsList1")}</List.Item>
                <List.Item>{t("warmSeasonsList2")}</List.Item>
                <List.Item>{t("warmSeasonsList3")}</List.Item>
                <List.Item>{t("warmSeasonsList4")}</List.Item>
                <List.Item>{t("warmSeasonsList5")}</List.Item>
              </List>
              <Text size="sm" c="dimmed">
                {t("warmSeasonsDescription")}
              </Text>
            </Box>
          </SimpleGrid>
        </Card>

        {/* Take the quiz */}
        <Card withBorder p="xl" radius="md" bg="violet.0">
          <Title order={2} mb="md">
            {t("takeQuizCtaTitle")}
          </Title>
          <Text mb="lg">
            {t("takeQuizCtaDescription")}
          </Text>
          <Group justify="space-between" wrap="wrap">
            <Box>
              <Text fw={600} size="lg">
                {t("mensColorAnalysisTitle")}
              </Text>
              <Text size="sm" c="dimmed">
                {t("mensColorAnalysisDescription")}
              </Text>
            </Box>
            <Group gap="md">
              <Button
                component={Link}
                href="/color-analysis-quiz"
                color="violet"
                rightSection={<IconPalette size={16} />}
              >
                {t("takeQuizButtonSmall")}
              </Button>
              <Button
                component={Link}
                href="/ai-color-analysis"
                color="violet"
                variant="outline"
                rightSection={<IconPhoto size={16} />}
              >
                {t("photoAnalysisButton")}
              </Button>
            </Group>
          </Group>
        </Card>

        {/* CTA */}
        <Box ta="center" py="xl">
          <Title order={2} mb="md">
            {t("ctaTitle")}
          </Title>
          <Text size="lg" c="dimmed" mb="xl">
            {t("ctaDescription")}
          </Text>
          <Group justify="center" gap="md">
            <Button
              size="xl"
              component={Link}
              href="/color-analysis-quiz"
              color="violet"
              rightSection={<IconPalette size={20} />}
            >
              {t("ctaQuizButton")}
            </Button>
            <Button
              size="xl"
              variant="outline"
              component={Link}
              href="/ai-color-analysis"
              color="violet"
              rightSection={<IconPhoto size={20} />}
            >
              {t("ctaPhotoButton")}
            </Button>
          </Group>
        </Box>
      </Stack>
    </Container>
  );
}
